import { forwardRef, MutableRefObject, useRef } from 'react'
import { Formik, FormikValues } from 'formik'
import { MemberProfile } from 'types'
import { FormikTextAreaField } from 'components/Form/TextAreaField'
import { FormikTextInputField } from 'components/Form/TextInputField'
import { FormikDatePickerField } from 'components/Form/DatePickerField'
import { Portal } from 'components/Portal/Portal'
import { Tooltip } from 'common/Tooltip/Tooltip'
import cx from 'classnames'
import moment, { Moment } from 'moment'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { validationSchema } from './validationSchema'
import { FormikSubmitButton } from '../Form/FormikSubmitButton'
import { ProfileHeader } from '../UserProfileCard/ProfileHeader'
import { CoverImage } from '../UserProfileCard/CoverImage'
import { ProfileImage } from '../UserProfileCard/ProfileImage'
import { FormikImageUploadField } from 'components/Form/ImageUploadField'
import { FormikImageUploadFieldModal } from 'components/Form/ImageUploadFieldModal'
import { FormikCheckbox } from 'components/Form/Checkbox/Checkbox'
import { FormikTimezoneSelectField } from '../Form/TimezoneSelect'
import { ImageOverlay } from '../UserProfileCard/ImageOverlay'
import Form, { APIFieldError, FormRef } from '../Form/Form'
import Styles from './WorkspaceMemberhsipProfileForm.module.scss'
import { useIsBlaze } from 'hooks/useIsBlaze'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

dayjs.extend(advancedFormat)

interface Props {
  profile: MemberProfile
  onSubmit: (values: FormikValues) => void
  submitButtonContainer: MutableRefObject<HTMLElement> | Element
  errors?: APIFieldError[]
}

export const WorkspaceMembershipProfileForm = forwardRef<FormRef, Props>(
  ({ profile, onSubmit, submitButtonContainer, errors = [] }: Props, ref) => {
    const { isMobile } = useResponsiveDesign()
    const dateTimeOptions = Intl.DateTimeFormat().resolvedOptions()
    const profileImageUrl = profile.profile_image_url !== null ? profile.profile_image_url : profile.avatar_url || ''
    const initialValues = {
      display_name: profile.display_name || profile.first_name || '',
      bio: profile.bio || '',
      role: profile.role || '',
      phone: profile.phone || '',
      birthday: profile.birthday ? moment(profile.birthday) : '',
      pronouns: profile.pronouns || '',
      cover_image_url: profile.cover_image_url || '',
      profile_image_url: profileImageUrl,
      twitter_handle: profile.twitter_handle || '@',
      timezone: profile.timezone || dateTimeOptions.timeZone,
      location: profile.location || '',
      team: profile.team || '',
      joined_date: profile.joined_date ? moment(profile.joined_date) : '',
      sync_profiles: profile.sync_profiles,
    }
    const containerRef = useRef(null)
    const isAlmanac = !useIsBlaze()

    const disabledDate = (currentDate: Moment | undefined) => {
      return currentDate ? currentDate > moment().endOf('day') : false
    }

    return (
      <Formik initialValues={initialValues} validationSchema={validationSchema} enableReinitialize onSubmit={onSubmit}>
        {({ values, setFieldValue }) => (
          <Form errors={errors} ref={ref}>
            <div className={Styles.profileHeader} ref={containerRef}>
              <ProfileHeader
                classNames={{
                  profileImage: Styles.profileImage,
                }}
                coverImage={
                  <FormikImageUploadField
                    cropSize={{
                      width: 500,
                      height: 150,
                    }}
                    labelContainerClassName={Styles.coverImageControls}
                    name="cover_image_url"
                    label="Cover Image"
                  >
                    {/* TODO: Fix this the next time the file is edited. */}
                    {/* @ts-ignore */}
                    {(url: string) => (
                      <ImageOverlay>
                        <CoverImage
                          coverImageUrl={url}
                          profileImageUrl={values.profile_image_url}
                          userId={profile.id}
                          alt={values.display_name}
                        />
                      </ImageOverlay>
                    )}
                  </FormikImageUploadField>
                }
                profileImage={
                  <FormikImageUploadFieldModal cropShape="round" name="profile_image_url" label="Profile Image">
                    {/* TODO: Fix this the next time the file is edited. */}
                    {/* @ts-ignore */}
                    {(url: string) => (
                      <ImageOverlay>
                        <ProfileImage profileImageUrl={url} alt={values.display_name} userId={profile.id} />
                      </ImageOverlay>
                    )}
                  </FormikImageUploadFieldModal>
                }
              />
            </div>
            {isAlmanac && (
              <>
                <FormikTextInputField
                  className={Styles.field}
                  label="Phone number"
                  name="phone"
                  placeholder="Your preferred phone number..."
                  size="lg"
                />
                <FormikTextInputField
                  className={Styles.field}
                  label="Location"
                  name="location"
                  placeholder="Let your team know where you're based..."
                  size="lg"
                />
                <FormikTimezoneSelectField
                  className={cx(Styles.field, Styles.inputOverrides)}
                  name="timezone"
                  label="Local time zone"
                  placeholder="Please select an option..."
                />
                <FormikTextAreaField
                  className={cx(Styles.field, Styles.inputOverrides)}
                  name="bio"
                  type="textarea"
                  label="Bio"
                  withCounter
                  maxLength={80}
                  rows={4}
                  placeholder="A brief bio..."
                />
              </>
            )}
            <FormikTextInputField className={Styles.field} name="display_name" label="Display Name" size="lg" />
            <FormikTextInputField
              className={Styles.field}
              label="Pronouns"
              name="pronouns"
              placeholder="..."
              size="lg"
            />
            {isAlmanac && (
              <>
                <FormikTextInputField
                  className={Styles.field}
                  label="Team"
                  name="team"
                  placeholder="What team are you on?"
                  size="lg"
                />
                <FormikTextInputField
                  className={Styles.field}
                  label="Role"
                  name="role"
                  placeholder="What's your role at the company?"
                  size="lg"
                />
                <FormikDatePickerField
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  getCalendarContainer={() => containerRef.current!}
                  className={Styles.field}
                  datePickerDropDownClassName={Styles.antdOverride}
                  label="Birthday"
                  name="birthday"
                  locale={dateTimeOptions.locale}
                  placeholder="Enter your birth date..."
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                  showToday={false}
                />
                <FormikDatePickerField
                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  getCalendarContainer={() => containerRef.current!}
                  className={Styles.field}
                  datePickerDropDownClassName={Styles.antdOverride}
                  label="Joined Date"
                  name="joined_date"
                  placeholder="When did you start working here..."
                  locale={dateTimeOptions.locale}
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                  showToday={false}
                />
                <FormikTextInputField className={Styles.field} label="Twitter handle" name="twitter_handle" size="lg" />
                <FormikCheckbox
                  onChange={(checked) => {
                    setFieldValue('sync_profiles', checked)
                  }}
                  className={cx(Styles.field, Styles.checkbox)}
                  size="md"
                  name="sync_profiles"
                >
                  <Tooltip label="Share this user profile data on all Workspaces" placement="top">
                    <span className={Styles.checkboxLabel}>Sync Profiles</span>
                  </Tooltip>
                </FormikCheckbox>
              </>
            )}
            <Portal container={submitButtonContainer}>
              <FormikSubmitButton size={isMobile ? 'xl' : 'lg'}>Save Changes</FormikSubmitButton>
            </Portal>
          </Form>
        )}
      </Formik>
    )
  },
)
